import React from "react";
import { Tooltip } from "@/components/common/Tooltip";

import CloseIcon from "@mui/icons-material/Close";
import { chipTextColor, resolveChipBg } from "./chipTextColor";

export interface CategoryChipProps {
  label: string;
  color: string | null;
  isDark: boolean;
  onClick?: () => void;
  title?: string;
  /** Appends a trailing close icon (visual only; the parent handles the click). */
  showRemoveIcon?: boolean;
}

/**
 * Pill-shaped label rendered with the category's stored color as background
 * and an auto-contrasted text color.
 *
 * If `onClick` is provided the element is a `<button>`; otherwise a `<span>`.
 */
export const CategoryChip: React.FC<CategoryChipProps> = ({
  label,
  color,
  isDark,
  onClick,
  title,
  showRemoveIcon,
}) => {
  const bg = resolveChipBg(color, isDark);
  const style = { backgroundColor: bg, color: chipTextColor(bg) } as const;
  const baseClass =
    "inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[11px] font-medium leading-tight whitespace-nowrap";

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={`${baseClass} cursor-pointer hover:opacity-80`}
        style={style}
      >
        <span>{label}</span>
        {showRemoveIcon &&
          (title ? (
            <Tooltip title={title}>
              <CloseIcon
                className="shrink-0 opacity-70"
                sx={{ fontSize: 12, color: "inherit" }}
              />
            </Tooltip>
          ) : (
            <CloseIcon
              className="shrink-0 opacity-70"
              sx={{ fontSize: 12, color: "inherit" }}
            />
          ))}
      </button>
    );
  }

  const span = (
    <span className={baseClass} style={style}>
      {label}
    </span>
  );
  return title ? <Tooltip title={title}>{span}</Tooltip> : span;
};
